import axios from 'axios';
import store from '@/store/store';
import { actions as _act } from '@/models/store.actions';
import { UnregisteredUserDocument } from '@/models/unregistered-user.models';
import { AppSignType } from '@/models/enums';
import { IDocumentActionUserDecisionResult } from '@/models/document-actions.model';

export const unregUserDocService = {
  getDocument,
  viewFile,
  downloadFile,
  downloadDocumentWithSignatures,
  downloadFileWithSignatures,
  downloadSignature,
  getSignatures,
  validateAuthorSignature,
  validateCounterpartySignature,
  validateCounterpartyAgreeSignature,
  validateCounterpartyAcquaintSignature,
  getHashesForSigningByCounterparty,
  updateDocumentVisualization,
  getDocumentVisualizationFiles,
  makeAcquaintDecision,
  makeAgreeDecision,
  makeSignDecision
};

let model = {
  get baseUrl() {
    return store.getters[_act.SETT_GET_APIURLGET];
  },
  controller: 'unregistered-users/documents'
};

function extract(r: any) {
  return r.data;
}

function getDocument(documentVersionCode: string): Promise<UnregisteredUserDocument> {
  return axios
    .get(`${model.baseUrl}${model.controller}/${documentVersionCode}`)
    .then((response) => response ? new UnregisteredUserDocument(response.data) : new UnregisteredUserDocument());
}

function viewFile(documentVersionCode: string, documentVersionsFileCode: string, isPdf: boolean): Promise<any> {
  return axios
    .get(`${model.baseUrl}${model.controller}/${documentVersionCode}/files/${documentVersionsFileCode}/view?isPdf=${isPdf}`)
    .then(extract);
}

function downloadFile(documentVersionCode: string, documentVersionsFileCode: string, isPdf: boolean): Promise<any> {
  return axios
    .get(`${model.baseUrl}${model.controller}/${documentVersionCode}/files/${documentVersionsFileCode}/download?isPdf=${isPdf}`)
    .then(extract);
}

async function downloadDocumentWithSignatures(documentVersionCode, actionsReport: boolean = false) {
  return axios
    .get(
      `${model.baseUrl}${model.controller}/${documentVersionCode}/download/zip?actionsReport=${actionsReport}`,
      { responseType: 'blob' });
}

async function downloadFileWithSignatures(documentVersionCode, documentVersionsFileCode: string) {
  return axios
    .get(
      `${model.baseUrl}${model.controller}/${documentVersionCode}/files/${documentVersionsFileCode}/download/zip`,
      { responseType: 'blob' });
}

async function downloadSignature(documentVersionCode: string, id: number, signSourceType: number, signType: AppSignType) {
  return axios
    .get(`${model.baseUrl}${model.controller}/${documentVersionCode}/signatures/${id}/download?signSourceType=${signSourceType}&signType=${signType}`,
      { responseType: 'blob' });
}

function getSignatures(documentVersionCode: string, documentVersionsFileCode: string): Promise<Array<any>> {
  if (documentVersionsFileCode) {
    return axios
      .get(`${model.baseUrl}${model.controller}/${documentVersionCode}/signatures/file/${documentVersionsFileCode}`)
      .then(extract);
  } else {
    return axios
      .get(`${model.baseUrl}${model.controller}/${documentVersionCode}/signatures`)
      .then(extract);
  }
}

function validateAuthorSignature(documentVersionCode: string, documentVerFilesSignId: number, signType: AppSignType): Promise<any> {
  return _validateSignature(documentVersionCode, documentVerFilesSignId, 1, signType);
}

function validateCounterpartySignature(documentVersionCode: string, documentUserSignsId: number, signType: AppSignType): Promise<any> {
  return _validateSignature(documentVersionCode, documentUserSignsId, 2, signType);
}

function validateCounterpartyAgreeSignature(documentVersionCode: string, documentAgreeUserSignsId: number,
  signType: AppSignType): Promise<any> {
  return _validateSignature(documentVersionCode, documentAgreeUserSignsId, 3, signType);
}

function validateCounterpartyAcquaintSignature(documentVersionCode: string, documentAcquaintUserSignsId: number,
  signType: AppSignType): Promise<any> {
  return _validateSignature(documentVersionCode, documentAcquaintUserSignsId, 4, signType);
}

async function _validateSignature(documentVersionCode: string, id: number, signSourceType: number, signType: AppSignType): Promise<any> {
  return axios
    .get(`${model.baseUrl}${model.controller}/${documentVersionCode}/signatures/${id}?signSourceType=${signSourceType}&signType=${signType}`)
    .then(extract);
}

function getHashesForSigningByCounterparty(documentVersionCode: string, keyType: number): Promise<any> {
  return axios
    .get(`${model.baseUrl}${model.controller}/${documentVersionCode}/signatures/hashes?keyType=${keyType}`)
    .then(extract);
}

function updateDocumentVisualization(documentVersionCode: string, documentVersionsFileCodes: Array<string>): Promise<boolean> {
  return axios
    .put(`${model.baseUrl}${model.controller}/${documentVersionCode}/signatures/visualizations/update`,
      { documentVersionsFileCodes })
    .then(extract);
}

function getDocumentVisualizationFiles(documentVersionCode: string): Promise<Array<string>> {
  return axios
    .get(`${model.baseUrl}${model.controller}/${documentVersionCode}/signatures/visualizations/files`)
    .then(extract);
}

function makeAcquaintDecision(documentVersionCode: string, documentActionUserId: number,
  documentActionUserStatusId: number): Promise<IDocumentActionUserDecisionResult> {
  return axios
    .post(`${model.baseUrl}${model.controller}/${documentVersionCode}/actions/acquaint/decision`,
      { documentActionUserId, documentActionUserStatusId })
    .then(extract);
}

function makeAgreeDecision(documentVersionCode: string, documentActionUserId: number,
  documentActionUserStatusId: number, description: string): Promise<IDocumentActionUserDecisionResult> {
  return axios
    .post(`${model.baseUrl}${model.controller}/${documentVersionCode}/actions/agree/decision`,
      { documentActionUserId, documentActionUserStatusId, description })
    .then(extract);
}

function makeSignDecision(documentVersionCode: string, documentActionUserId: number,
  documentActionUserStatusId: number, signs: any, stamps: any, description: string): Promise<IDocumentActionUserDecisionResult> {
  return axios
    .post(`${model.baseUrl}${model.controller}/${documentVersionCode}/actions/sign/decision`,
      { documentActionUserId, documentActionUserStatusId, signs, stamps, description })
    .then(extract);
}
